import {
  ArdoqId,
  FilterInterfaceFilter,
  FormattingFilter,
} from '@ardoq/api-types';
import { DependencyMapNode, LabelsData } from '../types';
import {
  getIconColorFromScopeData,
  nodeReferences,
  referencedNodes,
} from '../utils';
import { GET_CSS_BACKGROUND_OPTIONS } from '../consts';
import { LI_BACKGROUND_CLASS_NAME } from '@ardoq/global-consts';
import {
  referenceModelClassName,
  referenceTypeClassName,
  filterClass,
} from '@ardoq/color-helpers';
import { readableColor } from 'polished';
import { colors } from '@ardoq/design-tokens';
import {
  colorsAndRepresentations,
  componentUtils,
  workspaceUtils,
  type FilterColorData,
} from '@ardoq/scope-data';
import type { EnhancedScopeData } from '@ardoq/data-model';

/**
 * Calculates readable text colors based on the fill/background color for conditional formatting.
 *
 * @param fill - The fill/background color to calculate text colors against
 * @param isNewConditionalFormatting - Whether new conditional formatting is enabled
 * @returns An object containing textColor (for main text) and textModerateColor (for secondary text like reference labels),
 *          both using readableColor from polished to ensure WCAG contrast requirements.
 *          If conditional formatting is disabled, returns undefined values.
 *          If conditional formatting is enabled but no fill color is provided, returns default text colors.
 */
export const calculateTextColors = (
  fill: string | undefined,
  isNewConditionalFormatting?: boolean
): { textColor: string | undefined; textModerateColor: string | undefined } => {
  if (!isNewConditionalFormatting) {
    return { textColor: undefined, textModerateColor: undefined };
  }

  if (!fill || typeof fill !== 'string') {
    // When new conditional formatting is enabled but there's no fill color,
    // return default text colors to avoid white text on light backgrounds
    return {
      textColor: colors.textDefault,
      textModerateColor: colors.textModerate,
    };
  }

  return {
    textColor: readableColor(fill, colors.grey950, colors.white950),
    textModerateColor: readableColor(fill, colors.grey600, colors.white950),
  };
};

export const nodeFromComponentId = (
  modelId: ArdoqId,
  labelsData: LabelsData,
  isNewConditionalFormatting: boolean | undefined,
  scopeData: EnhancedScopeData,
  filterColorData: FilterColorData
): Omit<DependencyMapNode, 'id'> => {
  const className =
    componentUtils.getComponentCSSClassNames({
      scopeData,
      componentId: modelId,
      options: GET_CSS_BACKGROUND_OPTIONS,
      filterColorData,
    }) ?? '';

  const { fill, stroke } =
    componentUtils.getComponentDisplayColorAsSVGAttributes(
      scopeData,
      filterColorData,
      modelId,
      { ...GET_CSS_BACKGROUND_OPTIONS, isNewConditionalFormatting }
    );
  const { textColor, textModerateColor } = calculateTextColors(
    fill,
    isNewConditionalFormatting
  );
  const { mainLabel, otherLabels } =
    labelsData.nodeLabelsData.get(modelId) ?? {};

  return {
    modelId,
    children: null,
    className,
    fill,
    stroke,
    textColor,
    textModerateColor,
    iconColor: getIconColorFromScopeData(
      scopeData,
      filterColorData,
      modelId,
      modelId,
      false,
      isNewConditionalFormatting
    ),
    representationData: colorsAndRepresentations.getComponentRepresentationData(
      scopeData,
      modelId
    ) ?? {
      isImage: false,
      value: null,
    },
    label: mainLabel ?? '',
    isComponent: true,
    incomingReferences: null,
    outgoingReferences: null,
    combinedReferences: null,
    subLabel: null,
    referencesOmittedDueToRecursion: false,
    degreeOfParenthood: 0,
    degreeOfChildhood: 0,
    containsCollapsedNodes: false,
    otherLabels,
  };
};

export const hasNonComponentNodes = (node: DependencyMapNode): boolean =>
  !node.isComponent || Boolean(node.children?.some(hasNonComponentNodes));

/** @returns the component IDs of all nodes, node children, referenced nodes, children of referenced nodes, and references of child nodes, all the way down. */
function* nodeComponentIds(node: DependencyMapNode): Iterable<string> {
  if (node.isComponent) {
    yield node.modelId;
  }
  if (node.children) {
    for (const child of node.children) {
      yield* nodeComponentIds(child);
    }
  }
  for (const referencedNode of referencedNodes(node)) {
    yield* nodeComponentIds(referencedNode);
  }
}

export function* nodesComponentIds(
  nodes: Iterable<DependencyMapNode>
): Iterable<string> {
  for (const node of nodes) {
    yield* nodeComponentIds(node);
  }
}

/** @returns the reference IDs of all references, references of references, children of references, and references of children, all the way down. */
function* nodeReferenceIds(node: DependencyMapNode): Iterable<string> {
  for (const reference of nodeReferences(node)) {
    if (reference.referenceIds) {
      yield* reference.referenceIds;
    }
    yield* nodeReferenceIds(reference.node);
  }
  if (!node.children) {
    return;
  }
  for (const child of node.children) {
    yield* nodeReferenceIds(child);
  }
}

export function* nodesReferenceIds(
  nodes: Iterable<DependencyMapNode>
): Iterable<string> {
  for (const node of nodes) {
    yield* nodeReferenceIds(node);
  }
}

export const nodeReferenceTypeIdToLegendReferenceType =
  (
    formattingFilters: (FormattingFilter | FilterInterfaceFilter)[],
    scopeData: EnhancedScopeData
  ) =>
  (nodeReferenceTypeId: string) => {
    const [workspaceId, referenceTypeId] = nodeReferenceTypeId.split('-');
    const referenceType = workspaceUtils.getReferenceTypeById(
      scopeData,
      workspaceId,
      referenceTypeId
    );
    if (!referenceType) {
      return null;
    }
    const workspaceModelId =
      workspaceUtils.getWorkspaceModel(scopeData, workspaceId)?._id ?? '';

    const matchingFilter = formattingFilters.find(
      ({ affectReference, name, value }) =>
        affectReference && name === 'type' && value === referenceType.name
    );
    return {
      ...referenceType,
      globalTypeId: nodeReferenceTypeId,
      className: `integration ${LI_BACKGROUND_CLASS_NAME} ${referenceModelClassName(
        workspaceModelId
      )} ${referenceTypeClassName(referenceType.id)} ${
        matchingFilter?.color ? filterClass(matchingFilter.color) : ''
      }`,
    };
  };
